import React, { useEffect, useState, useRef } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import AOS from "aos";
import "aos/dist/aos.css";
import confetti from "canvas-confetti";
import { Tooltip, IconButton } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import checkIcon from "../../assets/images/icon/check.svg";
import shapeIcon1 from "../../assets/images/icon/1.png";

const sliderSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 1,
  responsive: [
    { breakpoint: 1024, settings: { slidesToShow: 2, slidesToScroll: 1 } },
    { breakpoint: 600, settings: { slidesToShow: 1, slidesToScroll: 1 } },
  ],
};

function OurPlans() {
  const [hoveredCard, setHoveredCard] = useState(null);
  const priceRefs = useRef([]);

  // Initialize AOS
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  // Count-up animation for price
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target;
            const finalValue = target.getAttribute("data-price");
            let start = 0;
            const increment = finalValue / 50;
            const timer = setInterval(() => {
              start += increment;
              if (start >= finalValue) {
                target.textContent = `$${finalValue}`;
                clearInterval(timer);
              } else {
                target.textContent = `$${Math.floor(start)}`;
              }
            }, 30);
            observer.unobserve(target);
          }
        });
      },
      { threshold: 0.5 }
    );

    priceRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Confetti on button click
  const handleChoosePlan = (e) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;

    confetti({
      particleCount: 80,
      spread: 70,
      origin: {
        x: x / window.innerWidth,
        y: y / window.innerHeight,
      },
      colors: ["#000080", "#000000", "#ffffff", "#6A0DAD"],
      gravity: 0.8,
      scalar: 1.1,
    });

    // Optional: redirect after 1s
    setTimeout(() => {
      window.location.href = "signup.html";
    }, 1000);
  };

  const plans = [
    {
      title: "Basic",
      price: 99,
      period: "Monthly",
      features: [
        { text: "Weekly online meeting", tooltip: "Join live Q&A every week" },
        { text: "Unlimited learning access", tooltip: "All courses, forever" },
        {
          text: "24/7 technical support",
          tooltip: "Chat, email, phone – anytime",
        },
        { text: "Personal training", tooltip: "1-on-1 strategy session" },
      ],
    },
    {
      title: "Standard",
      price: 149,
      period: "Monthly",
      active: true,
      features: [
        { text: "Weekly online meeting", tooltip: "Interactive live sessions" },
        { text: "Unlimited learning access", tooltip: "Full library access" },
        { text: "24/7 technical support", tooltip: "Priority response" },
        { text: "Business analysis", tooltip: "Market reports & insights" },
      ],
    },
    {
      title: "Premium",
      price: 199,
      period: "Monthly",
      features: [
        {
          text: "With all standard features",
          tooltip: "Everything in Standard",
        },
        { text: "Unlimited learning access", tooltip: "Lifetime updates" },
        { text: "24/7 technical support", tooltip: "Dedicated agent" },
        { text: "Personal training", tooltip: "Weekly 1-on-1 calls" },
      ],
    },
  ];

  return (
    <div>
      {/* GLOBAL STYLES */}
      <style>
        {`
    .pricing__item * { color: #000 !important; font-family: 'Poppins', sans-serif !important; }
    .pricing__item h3, .pricing__item h3 span { color: #000080 !important; }
    .pricing__item .pricing__list-item img {
      filter: brightness(0) saturate(100%) invert(12%) sepia(84%) saturate(7495%) hue-rotate(244deg) brightness(92%) contrast(106%) !important;
    }

    .pricing__item-inner {
      border: 2px solid transparent !important;
      transition: all 0.35s ease !important;
      border-radius: 16px;
      overflow: hidden;
      position: relative;
    }

    .pricing__item:hover .pricing__item-inner,
    .pricing__item:active .pricing__item-inner {
      border-image: linear-gradient(to right, #000080, #000000) 1 !important;
      box-shadow: 0 10px 28px rgba(0,0,128,0.3) !important;
    }

    /* BUTTON – LEFT TO RIGHT BLUE-BLACK GRADIENT FILL */
    .trk-btn--outline {
      background: transparent !important;
      color: #000 !important;
      border: 2px solid #000 !important;
      transition: all 0.35s ease !important;
      position: relative;
      overflow: hidden;
      font-weight: 600 !important;
      border-radius: 8px;
      padding: 12px 24px !important;
      z-index: 1;
    }
    .trk-btn--outline::before {
      content: "";
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: linear-gradient(to right, #000080, #000000) !important;
      opacity: 0;
      transition: opacity 0.35s ease;
      z-index: -1;
    }
    .trk-btn--outline:hover::before,
    .trk-btn--outline:active::before { opacity: 1; }
    .trk-btn--outline:hover,
    .trk-btn--outline:active {
      color: #fff !important;
      border-color: transparent !important;
    }

    .pricing__item.active .trk-btn--outline {
      background: linear-gradient(to right, #000080, #000000) !important;
      color: #fff !important;
      border: none !important;
    }

    @media (hover: none) {
      .pricing__item:active .pricing__item-inner {
        border-image: linear-gradient(to right, #000080, #000000) 1 !important;
        box-shadow: 0 6px 18px rgba(0,0,128,0.2) !important;
      }
      .trk-btn--outline:active {
        color: #fff !important;
        background: linear-gradient(to right, #000080, #000000) !important;
      }
    }

    .slick-dots li button:before { color: #000080; opacity: 0.5; }
    .slick-dots li.slick-active button:before { color: #000080; opacity: 1; }

    .feature-item { display: flex; align-items: center; gap: 8px; }
    .info-icon { font-size: 16px; color: #000080; opacity: 0.7; }
  `}
      </style>

      <section
        className="pricing padding-top padding-bottom"
        data-aos="fade-up"
      >
        <div className="section-header section-header--max50">
          <h2 className="mb-10 mt-minus-5">
            Our <span>pricings</span> plan
          </h2>
          <p>
            We offer the best pricings around – from installations to repairs,
            maintenance, and more!
          </p>
        </div>

        <div className="container">
          <Slider {...sliderSettings}>
            {plans.map((plan, index) => (
              <div key={index}>
                <div
                  className={`pricing__item ${plan.active ? "active" : ""}`}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div className="pricing__item-inner">
                    <div className="pricing__item-content">
                      <div className="pricing__item-top">
                        <h6 className="mb-15">{plan.title}</h6>
                        <h3 className="mb-25">
                          <span
                            ref={(el) => (priceRefs.current[index] = el)}
                            data-price={plan.price}
                            style={{
                              display: "inline-block",
                              minWidth: "80px",
                            }}
                          >
                            ${plan.price}
                          </span>
                          /<span>{plan.period}</span>
                        </h3>
                      </div>

                      <div className="pricing__item-middle">
                        <ul className="pricing__list">
                          {plan.features.map((feat, i) => (
                            <li
                              key={i}
                              className="pricing__list-item feature-item"
                            >
                              <img
                                src={checkIcon}
                                alt="check"
                                className="dark"
                              />
                              <span>{feat.text}</span>
                              <Tooltip
                                title={feat.tooltip}
                                arrow
                                placement="top"
                              >
                                <IconButton size="small" className="info-icon">
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="pricing__item-bottom">
                        <a
                          href="signup.html"
                          className="trk-btn trk-btn--outline"
                          onClick={handleChoosePlan}
                        >
                          Choose Plan
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>

        <div className="pricing__shape">
          <span className="pricing__shape-item pricing__shape-item--1">
            <span></span>
          </span>
          <span className="pricing__shape-item pricing__shape-item--2">
            <img src={shapeIcon1} alt="shape-icon" />
          </span>
        </div>
      </section>
    </div>
  );
}

export default OurPlans;
